import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import {
  Building2, Search, Plus, Pencil, Trash2, Eye, Users, Calendar,
  Sun, Moon, X, Save, AlertCircle, CheckCircle2, MapPin, Phone, Mail,
  TrendingUp, Clock, CheckCircle, PauseCircle, XCircle, IndianRupee,
} from "lucide-react";
import { authFetch } from "../utils/authFetch";


const API = process.env.REACT_APP_API_URL;

const emptyForm = {
  site_name: "", project_code: "", gst_number: "", address: "", city: "",
  start_date: "", end_date: "", project_value: "", status: "Ongoing",
  contact_person1_name: "", contact_person1_number: "", contact_person1_email: "",
  contact_person2_name: "", contact_person2_number: "", contact_person2_email: "",
  project_manager: "",
project_manager_mobile: "",

supervisor: "",
supervisor_mobile: "",

designers: "",
purchasers: "",
accountant: "",
};

const STATUSES = ["All", "Ongoing", "Completed", "On Hold", "Cancelled"];

function SitesPage() {
  const navigate = useNavigate();
  const currentRole = String(JSON.parse(localStorage.getItem("user") || "{}").role || "")
    .toLowerCase()
    .replace(/[\s-]+/g, "_");
  const isPurchaseTeam = currentRole === "purchase";
  const [sites, setSites] = useState([]);
  const [editingId, setEditingId] = useState(null);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("All");
  const [form, setForm] = useState(emptyForm);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);
  const [dark, setDark] = useState(
    typeof window !== "undefined" && localStorage.getItem("sp-theme") === "dark"
  );

  const t = dark
    ? { bg: "#111827", surface: "rgba(24,24,27,0.7)", card: "#18181b",
        border: "#27272a", borderSoft: "#1f1f23",
        text: "#fafafa", textMuted: "#a1a1aa", textFaint: "#71717a",
        chip: "#27272a", chipText: "#e4e4e7",
        rowHover: "rgba(39,39,42,0.5)", inputBg: "#18181b",
        primary: "#fafafa", primaryText: "#09090b",
        danger: "#f43f5e", success: "#10b981", warning: "#f59e0b", info: "#3b82f6" }
    : { bg: "#fafafa", surface: "rgba(255,255,255,0.7)", card: "#ffffff",
        border: "#e4e4e7", borderSoft: "#f4f4f5",
        text: "#09090b", textMuted: "#71717a", textFaint: "#a1a1aa",
        chip: "#f4f4f5", chipText: "#3f3f46",
        rowHover: "#fafafa", inputBg: "#ffffff",
        primary: "#09090b", primaryText: "#ffffff",
        danger: "#e11d48", success: "#059669", warning: "#d97706", info: "#2563eb" };

  useEffect(() => {
    localStorage.setItem("sp-theme", dark ? "dark" : "light");
    document.body.style.background = t.bg;
    document.body.style.color = t.text;
  }, [dark, t.bg, t.text]);

  const pushToast = (msg, type = "success") => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 2600);
  };

  useEffect(() => { loadSites(); }, []);

  const loadSites = async () => {
    try {
      const res = await authFetch(`${API}/sites`);
      const data = await res.json();
      setSites(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Error loading sites:", err);
      pushToast("Could not reach server", "error");
    }
  };

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const resetForm = () => { setForm(emptyForm); setEditingId(null); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (isPurchaseTeam) return;
    const sitePayload = {
      site_name: form.site_name, project_code: form.project_code.trim(), gst_number: form.gst_number,
      address: form.address, city: form.city,
      start_date: form.start_date || null, end_date: form.end_date || null,
      project_value: form.project_value === "" ? 0 : Number(form.project_value),
      status: form.status,
      contact_person1_name: form.contact_person1_name,
      contact_person1_number: form.contact_person1_number,
      contact_person1_email: form.contact_person1_email,
      contact_person2_name: form.contact_person2_name,
      contact_person2_number: form.contact_person2_number,
      contact_person2_email: form.contact_person2_email,
    };
    const teamPayload = {
  project_manager: form.project_manager,
  project_manager_mobile: form.project_manager_mobile,

  supervisor: form.supervisor,
  supervisor_mobile: form.supervisor_mobile,

  designers: form.designers,
  purchasers: form.purchasers,
  accountant: form.accountant,
};

    try {
      if (editingId) {
        await authFetch(`${API}/update-site/${editingId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sitePayload),
        });
        await authFetch(`${API}/update-site-team/${editingId}`, {
          method: "PUT", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(teamPayload),
        });
        pushToast("Project & team updated");
      } else {
        const res = await authFetch(`${API}/add-site`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify(sitePayload),
        });
        const savedSite = await res.json();
        const targetSiteId = savedSite.id || savedSite.site_id || (savedSite.site && savedSite.site.id);
        if (!targetSiteId) throw new Error("Backend returned no id");
        await authFetch(`${API}/add-site-team`, {
          method: "POST", headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ site_id: targetSiteId, ...teamPayload }),
        });
        pushToast("Project created successfully");
      }
      resetForm();
      setShowForm(false);
      loadSites();
    } catch (err) {
      console.error(err);
      pushToast(`Failed: ${err.message}`, "error");
    }
  };

  const handleEdit = async (site) => {
    if (isPurchaseTeam) return;
    try {
      const res = await authFetch(`${API}/site-full-details/${site.id}`);
      const data = await res.json();
      if (data) {
        setForm({
          site_name: data.site?.site_name || "",
          project_code: data.site?.project_code || "",
          gst_number: data.site?.gst_number || "",
          address: data.site?.address || "",
          city: data.site?.city || "",
          start_date: data.site?.start_date || "",
          end_date: data.site?.end_date || "",
          project_value: data.site?.project_value || "",
          status: data.site?.status || "Ongoing",
          contact_person1_name: data.site?.contact_person1_name || "",
          contact_person1_number: data.site?.contact_person1_number || "",
          contact_person1_email: data.site?.contact_person1_email || "",
          contact_person2_name: data.site?.contact_person2_name || "",
          contact_person2_number: data.site?.contact_person2_number || "",
          contact_person2_email: data.site?.contact_person2_email || "",
          project_manager: data.team?.project_manager || "",
          supervisor: data.team?.supervisor || "",
          designers: data.team?.designers || "",
          purchasers: data.team?.purchasers || "",
          accountant: data.team?.accountant || "",
          project_manager_mobile: data.team?.project_manager_mobile || "",
          supervisor_mobile: data.team?.supervisor_mobile || "",
        });
        setEditingId(site.id);
        setShowForm(true);
      }
    } catch {
      setForm({ ...emptyForm, ...site });
      setEditingId(site.id);
      setShowForm(true);
    }
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const doDelete = async () => {
    if (isPurchaseTeam) return;
    const id = confirmDel; setConfirmDel(null);
    try {
      await authFetch(`${API}/delete-site/${id}`, { method: "DELETE" });
      pushToast("Project deleted");
      loadSites();
    } catch { pushToast("Failed to delete", "error"); }
  };

  const filteredSites = useMemo(() => {
    return sites.filter((s) => {
      const matchSearch = [s.site_name, s.project_code, s.city, s.gst_number, s.contact_person1_name, s.project_manager, s.supervisor]
        .join(" ").toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "All" || s.status === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [sites, search, statusFilter]);

  const stats = useMemo(() => ({
    total: sites.length,
    ongoing: sites.filter((s) => s.status === "Ongoing").length,
    completed: sites.filter((s) => s.status === "Completed").length,
    value: sites.reduce((sum, s) => sum + (Number(s.project_value) || 0), 0),
  }), [sites]);

  const statusMeta = {
    Ongoing: { bg: t.info, icon: <Clock size={12} /> },
    Completed: { bg: t.success, icon: <CheckCircle size={12} /> },
    "On Hold": { bg: t.warning, icon: <PauseCircle size={12} /> },
    Cancelled: { bg: t.danger, icon: <XCircle size={12} /> },
  };

  // styles
  const s = {
    page: { minHeight: "100vh", background: t.bg, color: t.text,
      fontFamily:
"'Inter', system-ui, sans-serif",
      padding: "32px 24px", transition: "background .2s,color .2s" },
    wrap: {width: "100%"},
    header: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 12 },
    logoBox: { width: 44, height: 44, borderRadius: 14,
      background: dark ? "linear-gradient(135deg,#fff,#a1a1aa)" : "linear-gradient(135deg,#18181b,#3f3f46)",
      color: dark ? "#09090b" : "#fff", display: "flex", alignItems: "center", justifyContent: "center",
      boxShadow: dark ? "0 8px 24px rgba(255,255,255,.06)" : "0 8px 24px rgba(0,0,0,.12)" },
    h1: { margin: 0, fontSize: 24, fontWeight: 700, letterSpacing: "-0.02em" },
    sub: { margin: "2px 0 0", fontSize: 13, color: t.textMuted },
    iconBtn: { width: 40, height: 40, borderRadius: 12, border: `1px solid ${t.border}`,
      background: t.card, color: t.text, cursor: "pointer",
      display: "flex", alignItems: "center", justifyContent: "center", transition: "all .15s" },
    primaryBtn: { height: 40, padding: "0 18px", borderRadius: 12, border: "none",
      background: t.primary, color: t.primaryText, cursor: "pointer", fontSize: 13.5, fontWeight: 600,
      display: "flex", alignItems: "center", gap: 8,
      boxShadow: dark ? "0 4px 14px rgba(255,255,255,.08)" : "0 4px 14px rgba(0,0,0,.15)" },
    stats: { display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(220px,1fr))", gap: 16, marginBottom: 24 },
    statCard: { background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18,
      padding: "20px 22px", backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)",
      position: "relative", overflow: "hidden" },
    statLabel: { fontSize: 11, fontWeight: 600, letterSpacing: "0.08em", textTransform: "uppercase", color: t.textMuted },
    statValue: { fontSize: 30, fontWeight: 700, marginTop: 8, letterSpacing: "-0.02em" },
    statIcon: { position: "absolute", top: 20, right: 20, width: 32, height: 32, borderRadius: 10,
      background: t.chip, color: t.textMuted, display: "flex", alignItems: "center", justifyContent: "center" },
    section: { background: t.surface, border: `1px solid ${t.border}`, borderRadius: 18,
      backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", overflow: "hidden", marginBottom: 16 },
    sectionHead: { display: "flex", justifyContent: "space-between", alignItems: "center",
      padding: "16px 24px", borderBottom: `1px solid ${t.borderSoft}` },
    formGrid: { display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 24, padding: 24 },
    formCol: { display: "flex", flexDirection: "column", gap: 14 },
    grid2: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 },
    grid3: { display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 12 },
    fieldLabel: { display: "block", fontSize: 11, fontWeight: 600, letterSpacing: "0.08em",
      textTransform: "uppercase", color: t.textMuted, marginBottom: 6 },
    input: { width: "100%", height: 42, borderRadius: 10, border: `1px solid ${t.border}`,
      background: t.inputBg, color: t.text, padding: "0 14px", fontSize: 14,
      outline: "none", transition: "all .15s", boxSizing: "border-box", fontFamily: "inherit" },
    miniTitle: { fontSize: 11, fontWeight: 700, letterSpacing: "0.1em", textTransform: "uppercase",
      color: t.textMuted, margin: "8px 0 4px", display: "flex", alignItems: "center", gap: 8 },
    teamPanel: { background: t.chip, border: `1px solid ${t.border}`, borderRadius: 14, padding: 18,
      display: "flex", flexDirection: "column", gap: 12 },
    toolbar: { display: "flex", justifyContent: "space-between", alignItems: "center",
      gap: 12, marginBottom: 12, flexWrap: "wrap" },
    searchBox: { display: "flex", alignItems: "center", gap: 10,
      background: t.card, border: `1px solid ${t.border}`, borderRadius: 12,
      padding: "0 14px", height: 44, width: 340, maxWidth: "100%" },
    searchInput: { flex: 1, border: "none", outline: "none", background: "transparent",
      color: t.text, fontSize: 14, fontFamily: "inherit" },
    filterTab: (active) => ({ height: 36, padding: "0 14px", borderRadius: 10,
      border: `1px solid ${active ? t.primary : t.border}`,
      background: active ? t.primary : t.card,
      color: active ? t.primaryText : t.text,
      cursor: "pointer", fontSize: 12.5, fontWeight: 600, transition: "all .15s" }),
    table: { width: "100%", borderCollapse: "collapse", minWidth: 900 },
    th: { padding: "14px 24px", textAlign: "left", fontSize: 11, fontWeight: 600,
      letterSpacing: "0.08em", textTransform: "uppercase", color: t.textMuted,
      borderBottom: `1px solid ${t.borderSoft}`,
      background: dark ? "rgba(0,0,0,0.2)" : "rgba(250,250,250,0.6)" },
    td: { padding: "16px 24px", verticalAlign: "middle", borderBottom: `1px solid ${t.borderSoft}`, fontSize: 14 },
    avatar: { width: 36, height: 36, borderRadius: 10,
      background: dark ? "linear-gradient(135deg,#fff,#a1a1aa)" : "linear-gradient(135deg,#18181b,#3f3f46)",
      color: dark ? "#09090b" : "#fff",
      display: "flex", alignItems: "center", justifyContent: "center",
      fontWeight: 700, fontSize: 13, flexShrink: 0 },
    idChip: { fontFamily: "monospace", fontSize: 12, padding: "3px 8px", borderRadius: 6,
      background: t.chip, color: t.chipText },
    statusChip: (st) => ({ display: "inline-flex", alignItems: "center", gap: 5,
      padding: "4px 10px", borderRadius: 999, color: "#fff",
      background: statusMeta[st]?.bg || t.textMuted, fontSize: 11.5, fontWeight: 600 }),
    rowBtn: (color) => ({ width: 34, height: 34, borderRadius: 9, border: `1px solid ${t.border}`,
      background: t.card, color: color || t.text, cursor: "pointer",
      display: "inline-flex", alignItems: "center", justifyContent: "center",
      marginLeft: 4, transition: "all .15s" }),
  };

  return (
    <div style={s.page}>
      <style>{`
        @keyframes fadeIn { from{opacity:0} to{opacity:1} }
        @keyframes slideIn { from{opacity:0;transform:translateY(-10px)} to{opacity:1;transform:translateY(0)} }
        .sp-row:hover { background:${t.rowHover} !important; }
        .sp-icon:hover { background:${t.chip} !important; border-color:${t.textMuted} !important; }
        .sp-rowBtn:hover { background:${t.chip} !important; transform:translateY(-1px); }
        .sp-primary:hover { transform:translateY(-1px); }
        .sp-input:focus { border-color:${t.primary} !important; box-shadow:0 0 0 3px ${dark ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.06)"} !important; }
        .sp-input::placeholder { color:${t.textFaint}; }
        ::-webkit-scrollbar { width:10px; height:10px; }
        ::-webkit-scrollbar-thumb { background:${t.border}; border-radius:8px; }
      `}</style>

      {toast && (
        <div style={{ position: "fixed", top: 24, right: 24, zIndex: 100,
          display: "flex", alignItems: "center", gap: 10, padding: "12px 16px", borderRadius: 12,
          background: toast.type === "error" ? (dark ? "#3f1d1d" : "#fef2f2") : (dark ? "#0c2920" : "#ecfdf5"),
          border: `1px solid ${toast.type === "error" ? t.danger : t.success}`,
          color: toast.type === "error" ? t.danger : t.success,
          boxShadow: "0 10px 30px rgba(0,0,0,.15)", animation: "slideIn .25s",
          fontSize: 13.5, fontWeight: 500 }}>
          {toast.type === "error" ? <AlertCircle size={16} /> : <CheckCircle2 size={16} />}
          {toast.msg}
        </div>
      )}

      {!isPurchaseTeam && confirmDel != null && (
        <div style={{ position: "fixed", inset: 0, zIndex: 90, background: "rgba(0,0,0,.5)",
          backdropFilter: "blur(4px)", display: "flex", alignItems: "center", justifyContent: "center",
          animation: "fadeIn .18s" }}>
          <div style={{ background: t.card, border: `1px solid ${t.border}`, borderRadius: 18,
            padding: 28, width: "92%", maxWidth: 420, boxShadow: "0 20px 60px rgba(0,0,0,.3)" }}>
            <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Delete project?</h3>
            <p style={{ margin: "8px 0 22px", fontSize: 14, color: t.textMuted }}>
              This will permanently remove the project and its team assignments. This cannot be undone.
            </p>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <button onClick={() => setConfirmDel(null)}
                style={{ height: 40, padding: "0 16px", borderRadius: 10, border: `1px solid ${t.border}`,
                  background: t.card, color: t.text, cursor: "pointer", fontWeight: 600, fontSize: 13.5 }}>
                Cancel
              </button>
              <button onClick={doDelete}
                style={{ ...s.primaryBtn, background: t.danger, color: "#fff" }}>
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <div style={s.wrap}>
        {/* HEADER */}
        <div style={s.header}>
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <div style={s.logoBox}><Building2 size={20} /></div>
            <div>
              <h1 style={s.h1}>Project Management</h1>
              <p style={s.sub}>Manage all project sites, teams and client details</p>
            </div>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => setDark(!dark)} style={s.iconBtn} className="sp-icon" title="Toggle theme">
              {dark ? <Sun size={16} /> : <Moon size={16} />}
            </button>
            {!isPurchaseTeam && (
              <button onClick={() => { resetForm(); setShowForm(!showForm); }}
                style={s.primaryBtn} className="sp-primary">
                {showForm ? <X size={16} /> : <Plus size={16} />}
                {showForm ? "Close" : "New Project"}
              </button>
            )}
          </div>
        </div>

        {/* STATS */}
        <div style={s.stats}>
          <div style={s.statCard}>
            <div style={s.statIcon}><Building2 size={16} /></div>
            <div style={s.statLabel}>Total Projects</div>
            <div style={s.statValue}>{stats.total}</div>
          </div>
          <div style={s.statCard}>
            <div style={{ ...s.statIcon, color: t.info }}><Clock size={16} /></div>
            <div style={s.statLabel}>Ongoing</div>
            <div style={s.statValue}>{stats.ongoing}</div>
          </div>
          <div style={s.statCard}>
            <div style={{ ...s.statIcon, color: t.success }}><CheckCircle size={16} /></div>
            <div style={s.statLabel}>Completed</div>
            <div style={s.statValue}>{stats.completed}</div>
          </div>
          <div style={s.statCard}>
            <div style={s.statIcon}><TrendingUp size={16} /></div>
            <div style={s.statLabel}>Total Value</div>
            <div style={{ ...s.statValue, display: "flex", alignItems: "center", gap: 2 }}>
              <IndianRupee size={22} strokeWidth={2.5} />
              {stats.value >= 10000000 ? `${(stats.value / 10000000).toFixed(2)} Cr`
                : stats.value >= 100000 ? `${(stats.value / 100000).toFixed(2)} L`
                : stats.value.toLocaleString("en-IN")}
            </div>
          </div>
        </div>

        {/* FORM */}
        {!isPurchaseTeam && showForm && (
          <form onSubmit={handleSubmit} style={s.section}>
            <div style={s.sectionHead}>
              <div>
                <h2 style={{ margin: 0, fontSize: 16, fontWeight: 700 }}>
                  {editingId ? "Edit Project" : "Create New Project"}
                </h2>
                <p style={{ margin: "2px 0 0", fontSize: 12, color: t.textMuted }}>
                  Site details, contacts and EIMS team allocation
                </p>
              </div>
              <button type="button" onClick={() => { resetForm(); setShowForm(false); }}
                style={s.iconBtn} className="sp-icon"><X size={14} /></button>
            </div>

            <div style={s.formGrid}>
              {/* LEFT: Site details */}
              <div style={s.formCol}>
                <div style={s.miniTitle}><Building2 size={12} /> Site Details</div>
                <div style={s.grid2}>
                  <div>
                    <label style={s.fieldLabel}>Site Name *</label>
                    <input name="site_name" value={form.site_name} onChange={handleChange}
                      placeholder=" " required
                      style={s.input} className="sp-input" />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>Project Code *</label>
                    <input name="project_code" value={form.project_code} onChange={handleChange}
                      placeholder="101" required pattern="[0-9]{3}" maxLength="3" inputMode="numeric"
                      title="Enter a unique 3-digit Project Code" style={s.input} className="sp-input" />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>GST Number</label>
                    <input name="gst_number" value={form.gst_number} onChange={handleChange}
                      placeholder=" " style={s.input} className="sp-input" />
                  </div>
                </div>

                <div style={s.grid2}>
                  <div>
                    <label style={s.fieldLabel}>Address</label>
                    <input name="address" value={form.address} onChange={handleChange}
                      placeholder=" " style={s.input} className="sp-input" />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>City</label>
                    <input name="city" value={form.city} onChange={handleChange}
                      placeholder=" " style={s.input} className="sp-input" />
                  </div>
                </div>

                <div style={s.grid2}>
                  <div>
                    <label style={s.fieldLabel}>Project Value (₹)</label>
                    <input type="number" name="project_value" value={form.project_value}
                      onChange={handleChange} placeholder=" "
                      style={s.input} className="sp-input" />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>Status</label>
                    <select name="status" value={form.status} onChange={handleChange}
                      style={s.input} className="sp-input">
                      <option>Ongoing</option><option>Completed</option>
                      <option>On Hold</option><option>Cancelled</option>
                    </select>
                  </div>
                </div>

                <div style={s.grid2}>
                  <div>
                    <label style={s.fieldLabel}>Start Date</label>
                    <input type="date" name="start_date" value={form.start_date}
                      onChange={handleChange} style={s.input} className="sp-input" />
                  </div>
                  <div>
                    <label style={s.fieldLabel}>End Date</label>
                    <input type="date" name="end_date" value={form.end_date}
                      onChange={handleChange} style={s.input} className="sp-input" />
                  </div>
                </div>

                <div style={s.miniTitle}><Phone size={12} /> Contact Person 1</div>
                <div style={s.grid3}>
                  <input name="contact_person1_name" value={form.contact_person1_name}
                    onChange={handleChange} placeholder="Name" style={s.input} className="sp-input" />
                  <input name="contact_person1_number" value={form.contact_person1_number}
                    onChange={handleChange} placeholder="Phone" style={s.input} className="sp-input" />
                  <input type="email" name="contact_person1_email" value={form.contact_person1_email}
                    onChange={handleChange} placeholder="Email" style={s.input} className="sp-input" />
                </div>

                <div style={s.miniTitle}><Phone size={12} /> Contact Person 2</div>
                <div style={s.grid3}>
                  <input name="contact_person2_name" value={form.contact_person2_name}
                    onChange={handleChange} placeholder="Name" style={s.input} className="sp-input" />
                  <input name="contact_person2_number" value={form.contact_person2_number}
                    onChange={handleChange} placeholder="Phone" style={s.input} className="sp-input" />
                  <input type="email" name="contact_person2_email" value={form.contact_person2_email}
                    onChange={handleChange} placeholder="Email" style={s.input} className="sp-input" />
                </div>

                <div style={{ display: "flex", gap: 10, marginTop: 8 }}>
                  <button type="submit" style={s.primaryBtn} className="sp-primary">
                    <Save size={15} />{editingId ? "Update Project" : "Save Project"}
                  </button>
                  {editingId && (
                    <button type="button" onClick={() => { resetForm(); setShowForm(false); }}
                      style={{ height: 40, padding: "0 18px", borderRadius: 12,
                        border: `1px solid ${t.border}`, background: t.card, color: t.text,
                        cursor: "pointer", fontWeight: 600, fontSize: 13.5 }}>
                      Cancel
                    </button>
                  )}
                </div>
              </div>

              {/* RIGHT: Team panel */}
              <div>
                <div style={s.miniTitle}><Users size={12} /> EIMS Project Team</div>
                <div style={s.teamPanel}>
                  <div>
  <label style={s.fieldLabel}>Project Manager</label>
  <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 8 }}>
    <input
      name="project_manager"
      value={form.project_manager}
      onChange={handleChange}
      style={s.input}
      className="sp-input"
    />
    <input
      name="project_manager_mobile"
      value={form.project_manager_mobile || ""}
      onChange={handleChange}
      placeholder="Mobile"
      style={s.input}
      className="sp-input"
    />
  </div>
</div>

<div>
  <label style={s.fieldLabel}>Supervisor</label>
  <div style={{ display: "grid", gridTemplateColumns: "1fr 150px", gap: 8 }}>
    <input
      name="supervisor"
      value={form.supervisor}
      onChange={handleChange}
      style={s.input}
      className="sp-input"
    />
    <input
      name="supervisor_mobile"
      value={form.supervisor_mobile || ""}
      onChange={handleChange}
      placeholder="Mobile"
      style={s.input}
      className="sp-input"
    />
  </div>
</div>

<div>
  <label style={s.fieldLabel}>Designers</label>
  <input
    name="designers"
    value={form.designers}
    onChange={handleChange}
    style={s.input}
    className="sp-input"
  />
</div>

<div>
  <label style={s.fieldLabel}>Purchasers</label>
  <input
    name="purchasers"
    value={form.purchasers}
    onChange={handleChange}
    style={s.input}
    className="sp-input"
  />
</div>

<div>
  <label style={s.fieldLabel}>Accountant</label>
  <input
    name="accountant"
    value={form.accountant}
    onChange={handleChange}
    style={s.input}
    className="sp-input"
  />
</div>
                </div>
              </div>
            </div>
          </form>
        )}

        {/* TOOLBAR */}
        <div style={s.toolbar}>
          <div style={s.searchBox}>
            <Search size={16} color={t.textMuted} />
            <input style={s.searchInput} className="sp-input"
              placeholder="Search site, city, GST, contact, team…"
              value={search} onChange={(e) => setSearch(e.target.value)} />
            {search && (
              <button onClick={() => setSearch("")}
                style={{ background: "none", border: "none", color: t.textMuted, cursor: "pointer", padding: 0, display: "flex" }}>
                <X size={14} />
              </button>
            )}
          </div>
          <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
            {STATUSES.map((st) => (
              <button key={st} onClick={() => setStatusFilter(st)}
                style={s.filterTab(statusFilter === st)}>{st}</button>
            ))}
          </div>
        </div>

        {/* TABLE */}
        <div style={{ ...s.section, marginBottom: 0 }}>
          <div style={{ overflowX: "auto" }}>
            <table style={s.table}>
              <thead>
                <tr>
                  <th style={s.th}>ID</th>
                  <th style={s.th}>Project Code</th>
                  <th style={s.th}>Project</th>
                  <th style={s.th}>City</th>
                  <th style={s.th}>Status</th>
                  <th style={s.th}>Timeline</th>
                  <th style={s.th}>Value</th>
                  <th style={{ ...s.th, textAlign: "right" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredSites.length === 0 ? (
                  <tr><td colSpan="8" style={{ padding: "60px 24px", textAlign: "center", color: t.textMuted }}>
                    <div style={{ display: "inline-flex", flexDirection: "column", alignItems: "center", gap: 8 }}>
                      <div style={{ width: 48, height: 48, borderRadius: 14, background: t.chip,
                        display: "flex", alignItems: "center", justifyContent: "center", color: t.textFaint }}>
                        <Building2 size={20} />
                      </div>
                      <p style={{ margin: 0, fontWeight: 600, color: t.text }}>No projects found</p>
                      <p style={{ margin: 0, fontSize: 12 }}>Try adjusting your search or filters.</p>
                    </div>
                  </td></tr>
                ) : filteredSites.map((site) => (
                  <tr key={site.id} className="sp-row" style={{ transition: "background .15s" }}>
                    <td style={s.td}><span style={s.idChip}>#{site.id}</span></td>
                    <td style={s.td}><span style={s.idChip}>{site.project_code || "—"}</span></td>
                    <td style={s.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                        <div style={s.avatar}>{site.site_name?.[0]?.toUpperCase() || "?"}</div>
                        <div>
                          <div style={{ fontWeight: 600, color: t.text }}>{site.site_name}</div>
                          {site.contact_person1_name && (
                            <div style={{ fontSize: 12, color: t.textMuted, marginTop: 2 }}>
                              {site.contact_person1_name}
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td style={s.td}>
                      {site.city ? (
                        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: t.textMuted }}>
                          <MapPin size={13} /> {site.city}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={s.td}>
                      <span style={s.statusChip(site.status)}>
                        {statusMeta[site.status]?.icon}{site.status}
                      </span>
                    </td>
                    <td style={s.td}>
                      <div style={{ display: "flex", alignItems: "center", gap: 6, color: t.textMuted, fontSize: 13 }}>
                        <Calendar size={13} />
                        {new Date(site.start_date).toLocaleDateString("en-GB")} 
                        {site.end_date && <span style={{ color: t.textTransform }}>→ {new Date(site.end_date).toLocaleDateString("en-GB")}</span>}
                      </div>
                    </td>
                    <td style={s.td}>
                      {site.project_value ? (
                        <span style={{ fontWeight: 600, display: "inline-flex", alignItems: "center" }}>
                          <IndianRupee size={13} strokeWidth={2.5} />
                          {Number(site.project_value).toLocaleString("en-IN")}
                        </span>
                      ) : "—"}
                    </td>
                    <td style={{ ...s.td, textAlign: "right", whiteSpace: "nowrap" }}>
                      <button onClick={() => navigate(`/site-view/${site.id}`)}
                        style={s.rowBtn(t.info)} className="sp-rowBtn" title="View"><Eye size={14} /></button>
                      {!isPurchaseTeam && <>
                        <button onClick={() => handleEdit(site)}
                          style={s.rowBtn(t.warning)} className="sp-rowBtn" title="Edit"><Pencil size={14} /></button>
                        <button onClick={() => setConfirmDel(site.id)}
                          style={s.rowBtn(t.danger)} className="sp-rowBtn" title="Delete"><Trash2 size={14} /></button>
                      </>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {filteredSites.length > 0 && (
            <div style={{ padding: "14px 24px", borderTop: `1px solid ${t.borderSoft}`,
              fontSize: 12, color: t.textMuted, textAlign: "right" }}>
              Showing <strong style={{ color: t.text }}>{filteredSites.length}</strong> of {sites.length} projects
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default SitesPage;
